import React, { useEffect, useMemo } from 'react';

import { batch, useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import PriceCheckIcon from '@mui/icons-material/PriceCheck';

import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';

import { useRetrieveUserQuery } from '../../redux/features/authApi';

import { useGetInvsQuery } from '../../redux/features/invApi';

import { useGetExchangeRatesQuery } from '../../redux/features/exchangeRatesApi';

import { getContacts } from '../../redux/slices/contactSlice';

import { getItems as getBankAccounts } from '../../redux/slices/bankAccountSlice';

import { getItems as getItemsForItemInv } from '../../redux/slices/itemForItemInvSlice';

import { getItems as getNoteTerms } from '../../redux/slices/noteTermSlice';

import { getItems as getContractTerms } from '../../redux/slices/contractTermSlice';

import { getItems as getCurrencies } from '../../redux/slices/currencySlice';

import { getStatuses, setStatusesLocal, setStatusesLocalInv } from '../../redux/slices/statusSlice';

import { setPopup2 } from '../../redux/slices/popupSlice';

import { setItemForEdit } from '../../redux/slices/itemForEditSlice';

import Contact from '../Apm/Contact/Contact';
import FirstSteps from '../Apm/Dashboard/FirstSteps';
import Inv from '../Apm/Inv/Inv';
import Invoiced30Days from '../Apm/Dashboard/Invoiced30Days';
import Invoiced6Months from '../Apm/Dashboard/Invoiced6Months';

import Popup2 from '../Controls/Popups/Popup2';
import PopupQuote from '../Controls/Popups/PopupQuote';
import PopupInv from '../Controls/Popups/PopupInv';

import Quote from '../Apm/Quote/Quote';
import QuoteIcon from '../Data/Icons/QuoteIcon';

import { maxNumberAllowedPerSubscription, statusesInDiffLang } from '../Data/generalData';

import { euFlag, roFlag, ruFlag, uaFlag, usFlag } from '../Data/imageURL';

const SUBSCRIPTION_LIMIT_INDEXES = {
  quote: {
    active_free: 3,
    active_pro: 4,
    active_premium: 5,
  },

  invoice: {
    active_free: 6,
    active_pro: 7,
    active_premium: 8,
  },

  client: {
    active_free: 9,
    active_pro: 10,
    active_premium: 11,
  },
};

const getSubscriptionLimit = (type, subscriptionType) => {
  const index = SUBSCRIPTION_LIMIT_INDEXES[type]?.[subscriptionType];

  if (typeof index !== 'number') {
    return null;
  }

  return maxNumberAllowedPerSubscription[index];
};

const hasReachedSubscriptionLimit = (type, subscriptionType, currentCount) => {
  const limit = getSubscriptionLimit(type, subscriptionType);

  return typeof limit === 'number' && currentCount >= limit;
};

const isValidDate = (date) => {
  return date instanceof Date && !Number.isNaN(date.getTime());
};

const isCurrentMonth = (dateValue) => {
  const date = new Date(dateValue);
  const now = new Date();

  if (!isValidDate(date)) {
    return false;
  }

  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

const getRateMap = (exchangeRates) => {
  const rates = exchangeRates?.Valute ?? [];

  return rates.reduce(
    (result, rate) => {
      result[rate.CharCode] = Number(rate.Value) || 0;

      return result;
    },
    {
      MDL: 1,
    }
  );
};

const getInvoiceAmount = (invoice) => {
  return (invoice?.iteminv_invs ?? []).reduce((total, item) => {
    const amount = Number(item?.amount) || 0;

    const quantity = Number(item?.quantity) || 1;

    const vat = Number(item?.item_for_item_inv?.vat) || 0;

    const subtotal = amount * quantity;

    return total + subtotal + subtotal * (vat / 100);
  }, 0);
};

const getInvoiceAmountInMdl = (invoice, rateMap) => {
  const currencyCode = invoice?.currency?.currencycode || 'MDL';

  const exchangeRate = currencyCode === 'MDL' ? 1 : Number(rateMap[currencyCode]) || 0;

  return getInvoiceAmount(invoice) * exchangeRate;
};

const createMonthBucket = (invoices, monthOffset) => {
  const now = new Date();

  const start = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);

  const end = new Date(now.getFullYear(), now.getMonth() - monthOffset + 1, 1);

  return invoices.filter((invoice) => {
    const invoiceDate = new Date(invoice?.date_inv);

    return isValidDate(invoiceDate) && invoiceDate >= start && invoiceDate < end;
  });
};

const buildDashboardStats = (invoices, exchangeRates, locale) => {
  const validInvoices = (invoices ?? []).filter((invoice) => invoice?.quote !== true);

  const rateMap = getRateMap(exchangeRates);

  /*
   * Preserve the structure expected by the existing
   * Invoiced6Months component:
   *
   * indexes 0–5  = current month through five months ago
   * indexes 6–11 = same six months one year earlier
   */
  const currentOffsets = [0, 1, 2, 3, 4, 5];

  const previousYearOffsets = [12, 13, 14, 15, 16, 17];

  const filteredArray = [...currentOffsets, ...previousYearOffsets].map((offset) =>
    createMonthBucket(validInvoices, offset)
  );

  const arraySum = filteredArray.map((bucket) =>
    bucket.reduce((total, invoice) => total + getInvoiceAmountInMdl(invoice, rateMap), 0)
  );

  const now = new Date();

  const firstDayOfYear = new Date(now.getFullYear(), 0, 1);

  const yearToDateInvoices = validInvoices.filter((invoice) => {
    const invoiceDate = new Date(invoice?.date_inv);

    return isValidDate(invoiceDate) && invoiceDate >= firstDayOfYear && invoiceDate <= now;
  });

  const yearToDateTotal = yearToDateInvoices.reduce(
    (total, invoice) => total + getInvoiceAmountInMdl(invoice, rateMap),
    0
  );

  const graphMonths = [5, 4, 3, 2, 1, 0].map((monthOffset) => {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);

    return monthDate.toLocaleString(locale, {
      month: 'short',
    });
  });

  return {
    filteredArray,
    arraySum,
    arrayYearToDateSum: [yearToDateTotal],
    graphMonths,
  };
};

const getCurrencyFlag = (currencyCode) => {
  switch (currencyCode) {
    case 'EUR':
      return euFlag;

    case 'USD':
      return usFlag;

    case 'RUB':
      return ruFlag;

    case 'RON':
      return roFlag;

    case 'UAH':
      return uaFlag;

    default:
      return uaFlag;
  }
};

const SummaryRow = ({ icon, label, value }) => {
  return (
    <>
      <Grid container item xs={12}>
        <Grid
          item
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: '65%',
              }}
            >
              <Stack direction="row">
                {icon}

                <Typography
                  variant="body1"
                  sx={{
                    ml: 1,
                    pt: 0.5,
                    pb: 1,
                    fontWeight: 500,
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'end',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  pt: 0.5,
                  pr: 1,
                  pb: 1,
                  fontWeight: 500,
                }}
              >
                {value}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider />
    </>
  );
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  /*
   * RTK Query server state
   */
  const { data: account, isFetching: isAccountFetching } = useRetrieveUserQuery();

  const { data: invoicePage, isFetching: areInvoicesFetching } = useGetInvsQuery({
    quote: false,
    limit: 100,
  });

  const { data: quotePage, isFetching: areQuotesFetching } = useGetInvsQuery({
    quote: true,
    limit: 100,
  });

  const invoices = useMemo(() => invoicePage?.results ?? [], [invoicePage]);
  const quotes = useMemo(() => quotePage?.results ?? [], [quotePage]);
  const invoiceCount = useMemo(() => invoicePage?.count ?? invoices.length, [invoicePage, invoices.length]);

  const { data: exchangeRates, isFetching: areRatesFetching } = useGetExchangeRatesQuery();

  /*
   * UI state only
   */
  const { popup2, popupQuote, popupInv, selectedInv, selectedQuote } = useSelector((state) => state.popupSlice);

  const { itemForEdit } = useSelector((state) => state.itemForEditSlice);

  /*
   * Legacy lookup state.
   * These can be migrated to RTK Query separately.
   */
  const { contacts, statusContacts } = useSelector((state) => state.contactSlice);

  const { bankAccounts, statusBankAccounts } = useSelector((state) => state.bankAccountSlice);

  const { itemsForItemInv, statusItemsForItemInv } = useSelector((state) => state.itemForItemInvSlice);

  const { noteTerms, statusNoteTerms } = useSelector((state) => state.noteTermSlice);

  const { contractTerms, statusContractTerms } = useSelector((state) => state.contractTermSlice);

  const { currencies, statusCurrencies } = useSelector((state) => state.currencySlice);

  const { statuses, statusStatuses } = useSelector((state) => state.statusSlice);

  const company = account?.company ?? null;

  const subscriptionType = account?.subscription_type ?? null;

  const companyUsers = account?.company_users ?? account?.groups?.[0]?.user_set ?? [];

  /*
   * Temporary lookup loading.
   *
   * Remove this effect after these resources get their
   * own RTK Query endpoints.
   */
  useEffect(() => {
    batch(() => {
      if (contacts.length < 1 && statusContacts === null) {
        dispatch(getContacts('/'));
      }

      if (bankAccounts.length < 1 && statusBankAccounts === null) {
        dispatch(getBankAccounts('/'));
      }

      if (itemsForItemInv.length < 1 && statusItemsForItemInv === null) {
        dispatch(getItemsForItemInv('/'));
      }

      if (noteTerms.length < 1 && statusNoteTerms === null) {
        dispatch(getNoteTerms('/'));
      }

      if (contractTerms.length < 1 && statusContractTerms === null) {
        dispatch(getContractTerms('/'));
      }

      if (statuses.length < 1 && statusStatuses === null) {
        dispatch(getStatuses());
      }

      if (currencies.length < 1 && statusCurrencies === null) {
        dispatch(getCurrencies('/'));
      }
    });
  }, [
    dispatch,
    contacts.length,
    statusContacts,
    bankAccounts.length,
    statusBankAccounts,
    itemsForItemInv.length,
    statusItemsForItemInv,
    noteTerms.length,
    statusNoteTerms,
    contractTerms.length,
    statusContractTerms,
    statuses.length,
    statusStatuses,
    currencies.length,
    statusCurrencies,
  ]);

  /*
   * Keep localized statuses in the legacy status slice until
   * SelectBasic and related forms are migrated.
   */
  useEffect(() => {
    if (!statuses.length) {
      return;
    }

    const language = (i18n.resolvedLanguage || i18n.language || 'ro').split('-')[0];

    const labels = statusesInDiffLang();

    const quoteLabelRanges = {
      en: labels.slice(0, 4),
      ro: labels.slice(4, 8),
      ru: labels.slice(8, 12),
    };

    const invoiceLabelRanges = {
      en: labels.slice(12, 17),
      ro: labels.slice(17, 22),
      ru: labels.slice(22, 27),
    };

    const quoteStatuses = statuses.filter(
      (status) => Number(status.serial_number) >= 100 && Number(status.serial_number) < 200
    );

    const invoiceStatuses = statuses.filter((status) => Number(status.serial_number) >= 200);

    dispatch(
      setStatusesLocal(
        quoteStatuses.map((status, index) => ({
          ...status,
          type: quoteLabelRanges[language]?.[index] ?? '',
        }))
      )
    );

    dispatch(
      setStatusesLocalInv(
        invoiceStatuses.map((status, index) => ({
          ...status,
          type: invoiceLabelRanges[language]?.[index] ?? '',
        }))
      )
    );
  }, [dispatch, i18n.language, i18n.resolvedLanguage, statuses]);

  const dashboardStats = useMemo(
    () => buildDashboardStats(invoices, exchangeRates, i18n.resolvedLanguage || i18n.language || 'ro'),
    [invoices, exchangeRates, i18n.language, i18n.resolvedLanguage]
  );

  const quotesIssuedThisMonth = useMemo(
    () => quotes.filter((quoteItem) => isCurrentMonth(quoteItem?.date_inv)).length,
    [quotes]
  );

  const invoicesIssuedThisMonth = dashboardStats.filteredArray?.[0]?.length ?? 0;

  const yearToDateTotal = dashboardStats.arrayYearToDateSum?.[0] ?? 0;

  const disabledAddButtonQuote = hasReachedSubscriptionLimit('quote', subscriptionType, quotesIssuedThisMonth);

  const disabledAddButtonInv = hasReachedSubscriptionLimit('invoice', subscriptionType, invoicesIssuedThisMonth);

  const disabledAddButtonClient = hasReachedSubscriptionLimit('client', subscriptionType, contacts.length);

  const isDashboardFetching = isAccountFetching || areInvoicesFetching || areQuotesFetching || areRatesFetching;

  const addOrEditClient = () => {
    batch(() => {
      dispatch(setPopup2(false));
      dispatch(setItemForEdit(null));
    });
  };

  return (
    <>
      {isDashboardFetching && <LinearProgress />}

      <Grid
        container
        item
        spacing={2}
        sx={{
          mt: 0,
          height: '100%',
        }}
      >
        <Grid item lg={4} md={4} xl={4} xs={12}>
          <FirstSteps
            disabledAddButtonInv={disabledAddButtonInv}
            disabledAddButtonQuote={disabledAddButtonQuote}
            disabledAddButtonClient={disabledAddButtonClient}
            bankAccounts={bankAccounts}
            invLength={invoiceCount}
          />
        </Grid>

        <Grid item lg={4} md={4} xl={4} xs={12}>
          <Card
            sx={{
              height: 520,
              maxWidth: 'auto',
            }}
          >
            <CardHeader
              title={company?.companyname || t('dashboard.sales')}
              subheader=" "
              sx={{
                backgroundColor: '#f1f1f1',
                height: 85,
                pt: 0,
              }}
            />

            <Divider />

            <CardContent>
              <SummaryRow
                icon={<QuoteIcon fill="#757575" />}
                label={t('dashboard.quotes_issued_this_month_colon')}
                value={quotesIssuedThisMonth}
              />

              <SummaryRow
                icon={<ReceiptLongIcon />}
                label={t('dashboard.invoices_issued_this_month_colon')}
                value={invoicesIssuedThisMonth}
              />

              <SummaryRow icon={<PeopleAltIcon />} label={t('dashboard.clients_colon')} value={contacts.length} />

              <SummaryRow
                icon={<PriceCheckIcon />}
                label={t('dashboard.goods_services_colon')}
                value={itemsForItemInv.length}
              />

              <SummaryRow icon={<PeopleOutlineIcon />} label={t('dashboard.users_colon')} value={companyUsers.length} />

              <SummaryRow
                icon={<AttachMoneyIcon />}
                label={t('dashboard.total_invoiced_this_year')}
                value={
                  Number.isFinite(yearToDateTotal)
                    ? `${yearToDateTotal.toLocaleString('en-US', {
                        maximumFractionDigits: 0,
                      })} MDL`
                    : '0 MDL'
                }
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item lg={4} md={4} xl={4} xs={12}>
          <Card
            sx={{
              height: 520,
              maxWidth: 'auto',
            }}
          >
            <CardHeader
              title={t('dashboard.exchange_rates_nbm')}
              subheader={`${t('dashboard.exchange_rate_updated_for')} ${new Date().toLocaleDateString('en-GB')}`}
              subheaderTypographyProps={{
                fontWeight: 500,
              }}
              sx={{
                backgroundColor: '#f1f1f1',
                minHeight: 85,
              }}
            />

            <Divider />

            <CardContent>
              <TableContainer>
                <Table
                  sx={{
                    width: '100%',
                  }}
                  aria-label="exchange rates"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inv.currency')}</TableCell>

                      <TableCell align="right">{t('dashboard.exchange_rate_nbm')}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(exchangeRates?.Valute ?? []).map((row) => (
                      <TableRow
                        key={row.CharCode}
                        sx={{
                          '&:last-child td, &:last-child th': {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              gap: '10px',
                            }}
                          >
                            <img
                              src={getCurrencyFlag(row.CharCode)}
                              alt={row.CharCode}
                              style={{
                                width: 30,
                                height: 'auto',
                              }}
                            />

                            {row.CharCode}
                          </Box>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {row.Value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider />
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={6} xl={6}>
          <Invoiced6Months filtered={dashboardStats} />
        </Grid>

        <Grid item xs={12} md={12} lg={6} xl={6}>
          <Invoiced30Days invoices={invoices} exchangeRates={exchangeRates} />
        </Grid>
      </Grid>

      {popupQuote && selectedQuote && (
        <PopupQuote
          title={`${t(smMediaDown ? 'quote.quote_hash_short' : 'quote.quote_hash')} ${selectedQuote.qn || ''}`}
        >
          <Quote initialValue={selectedQuote} />
        </PopupQuote>
      )}

      {popupInv && selectedInv && (
        <PopupInv title={`${t(smMediaDown ? 'inv.invoice_hash_short' : 'inv.invoice_hash')} ${selectedInv.vn || ''}`}>
          <Inv initialValue={selectedInv} />
        </PopupInv>
      )}

      {popup2 && itemForEdit?.fieldName === 'bill_to' && (
        <Popup2 title={t('popup.title.client_details')}>
          <Contact addOrEdit={addOrEditClient} />
        </Popup2>
      )}
    </>
  );
};

export default Dashboard;
